import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { AcsTransactionResult, TransactionResult } from '../types/TransactionResult'
import { sendAppleToken, validateMerchantSession } from '../api/payApple'
import { redirectToAcs } from '../utils/redirectToAcs'
import { createLogger } from '../utils/logger'

const logger = createLogger('useApplePaySession')

export const useApplePaySession = (paymentPageId: string) => {
    const navigate = useNavigate()

    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const startApplePay = useCallback((transaction: TransactionResult) => {
        setIsProcessing(true)
        setError(null)

        try {
            const paymentRequest = {
                countryCode: 'EE',
                currencyCode: transaction.currency.toUpperCase(),
                supportedNetworks: ['visa', 'masterCard'],
                merchantCapabilities: ['supports3DS'],
                total: {
                    label: 'Payment for order #' + transaction.transactionId,
                    amount: (transaction.amount / 100).toFixed(2),
                },
            }

            logger.debug('Payment request:', paymentRequest)

            const ApplePaySession = window.ApplePaySession
            const session = new ApplePaySession(3, paymentRequest)

            session.onvalidatemerchant = async (event: any) => {
                try {
                    logger.debug('Validation URL:', event.validationURL)

                    const merchantSession = await validateMerchantSession(
                        paymentPageId,
                        transaction.transactionId,
                        event.validationURL,
                    )

                    logger.debug('Merchant session received:', merchantSession)

                    session.completeMerchantValidation(merchantSession)
                } catch (err) {
                    logger.error('Merchant validation failed:', err)

                    session.abort()

                    setIsProcessing(false)
                    setError(t`Ошибка валидации мерчанта.`)
                }
            }

            session.onpaymentauthorized = async (event: any) => {
                try {
                    logger.debug('Payment token:', event.payment.token)

                    const result = await sendAppleToken(
                        paymentPageId,
                        transaction.transactionId,
                        event.payment.token,
                    )

                    logger.debug('Result:', result)

                    if (result.type === 'init' && 'acsUrl' in result) {
                        session.completePayment(ApplePaySession.STATUS_SUCCESS)

                        redirectToAcs(result as AcsTransactionResult)

                        return
                    }

                    if (result.type === 'payment' || result.type === 'pending') {
                        session.completePayment(ApplePaySession.STATUS_SUCCESS)
                    } else {
                        session.completePayment(ApplePaySession.STATUS_FAILURE)

                        setError(t`Платеж отклонен. Попробуйте другую карту.`)
                        setIsProcessing(false)
                    }

                    navigate(`/result/${paymentPageId}/${transaction.transactionId}`)
                } catch (err) {
                    logger.error('Payment processing failed:', err)

                    session.completePayment(ApplePaySession.STATUS_FAILURE)

                    setError(t`Ошибка обработки платежа. Попробуйте еще раз.`)
                    setIsProcessing(false)
                }
            }

            session.oncancel = (event: any) => {
                logger.info('Payment cancelled:', event)

                setIsProcessing(false)
                setError(t`Оплата отменена`)
            }

            session.begin()
        } catch (err) {
            logger.error('Apple Pay error:', err)

            setError(t`Не удалось инициировать платеж. Попробуйте позже.`)
            setIsProcessing(false)
        }
    }, [paymentPageId, navigate])

    const resetError = useCallback(() => {
        setError(null)
    }, [])

    return {
        startApplePay,
        isProcessing,
        error,
        resetError,
    }
}
